import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import PropTypes from "prop-types";
import { TASK_STATUS } from "../../constants";
import { parseApiError } from "../../services/apiError";
import TaskActivity from "./TaskActivity";
import TaskAttachments from "./TaskAttachments";
import TaskDrawings from "./TaskDrawings";
import { displayName, isAgent, userLabel, relativeTime } from "./userDisplay";
import UserAvatar from "../people/UserAvatar";
import { statusLabel } from "./activityText";
import { ARCHIVE_AFTER_DAYS } from "./archivePolicy";

/**
 * Create/edit modal for tasks.
 *
 * Props:
 *   - show / onHide         — visibility control
 *   - projects              — list used to populate the project dropdown
 *   - task                  — if provided, opens in edit mode
 *   - defaultProjectId      — create-mode default for the project field;
 *                             the parent passes the current board scope
 *                             (a project id, or null for "all" / "none")
 *   - defaultTitle / defaultDescription — create mode: what the form
 *                             starts with ("Make this a task" from a post)
 *   - onSave(data)          — parent-supplied callback; for edit mode the
 *                             parent binds to updateTask(task.id, data),
 *                             for create mode to createTask(data)
 *   - onDelete(id)          — edit mode only; called after the two-step
 *                             inline confirm flow
 *   - users                 — the household directory (assignee picker)
 *   - me                    — the signed-in user's id; drives which
 *                             buttons show (Delete for owners, Release
 *                             for the claimant/owner). Null = unknown →
 *                             show everything and let the API decide.
 *   - onArchive(id) / onUnarchive(id) — edit mode; archive a finished
 *                             task (the parent closes the dialog) / restore
 *                             an archived one (resolves with the task)
 *   - onAttachmentsChange() — edit mode; a file was attached or removed
 *                             (the board refetches for the card's count)
 *   - onCloseAutoFocus(event) — Radix's close-autofocus hook: a parent
 *                             that moves focus itself after a save (a
 *                             discussion post's new task link) calls
 *                             event.preventDefault() so the opener doesn't
 *                             take it back a frame later
 *   - onClaim(id) / onRelease(id) — edit mode; resolve with the updated task
 *
 * Sharing rules the modal reflects (spec P3/P1): anyone who can see a task
 * may edit it; only its owner or the project's owner may delete it; the
 * claim is released by the claimant or either owner.
 * Validation errors from the API are surfaced inline on the relevant
 * field when the response body looks like DRF field errors
 * (`{ title: ["..."] }`), or as a form-level banner otherwise.
 */
const STATUS_OPTIONS = [
  { value: TASK_STATUS.TODO, label: "TODO" },
  { value: TASK_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: TASK_STATUS.BLOCKED, label: "Blocked" },
  { value: TASK_STATUS.DONE, label: "Done" },
  { value: TASK_STATUS.CANCELED, label: "Canceled" },
];

// Format an ISO datetime for a <input type="datetime-local"> value.
const toDateTimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CLOSED = new Set([TASK_STATUS.DONE, TASK_STATUS.CANCELED]);

// The fields that are the user's to edit — the live modal never overwrites
// one you've touched (see the conflict notice). Status and assignee are
// the server's to move, as before.
const LIVE_FIELDS = [
  "title",
  "description",
  "priority",
  "dueDate",
  "projectId",
];
const FIELD_IDS = {
  title: "task-title",
  description: "task-description",
  priority: "task-priority",
  dueDate: "task-due-date",
  projectId: "task-project",
};
// Two values of a field are the same edit when Save would send the same
// thing: text fields are trimmed on save, so "New " and "New" agree.
const sameValue = (field, a, b) =>
  field === "title" || field === "description"
    ? String(a).trim() === String(b).trim()
    : String(a) === String(b);
const FIELD_LABELS = {
  title: "title",
  description: "description",
  priority: "priority",
  dueDate: "due date",
  projectId: "project",
};

function TaskModal({
  show,
  onHide,
  projects,
  task,
  defaultProjectId,
  defaultTitle = "",
  defaultDescription = "",
  onSave,
  onDelete,
  users = [],
  usersError = null,
  onRetryUsers = undefined,
  me = null,
  onClaim = undefined,
  onRelease = undefined,
  onAttachmentsChange = undefined,
  onArchive = undefined,
  onUnarchive = undefined,
  onCloseAutoFocus = undefined,
}) {
  const isEdit = Boolean(task);
  const taskId = task?.id ?? null;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(TASK_STATUS.TODO);
  const [priority, setPriority] = useState(500);
  const [dueDate, setDueDate] = useState("");
  const [projectId, setProjectId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [claimBusy, setClaimBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Bumped when a file is attached/removed so the thread shows the event
  // (task.updated_at doesn't change for attachments).
  const [threadBump, setThreadBump] = useState(0);
  // Someone else changed this task's files or drawing links (the live
  // thread saw the event): bumping these re-lists them quietly.
  const [filesKey, setFilesKey] = useState(0);
  const [drawingsKey, setDrawingsKey] = useState(0);
  // Fields someone else changed while you were editing the same field:
  // field -> their value (projects/task-live-refresh D8). Never applied
  // silently; the notice offers Load theirs / Keep mine.
  const [conflicts, setConflicts] = useState({});
  // Uploads in flight: Save and close wait for them (closing would cancel
  // them silently). TaskAttachments reports it via onBusyChange.
  const [uploadsBusy, setUploadsBusy] = useState(false);
  // What the form looked like when it opened (or after a claim/release
  // refreshed status/assignee). Save sends ONLY fields that differ from
  // this, so an edit made while an agent claimed the task can't write the
  // pre-claim status/assignee back over the agent's work (spec P11).
  const initialRef = useRef(null);
  const titleRef = useRef(null);

  const formFromTask = (t) => ({
    title: t.title || "",
    description: t.description || "",
    status: t.status || TASK_STATUS.TODO,
    priority: t.priority ?? 500,
    dueDate: toDateTimeLocal(t.due_date),
    projectId: t.project != null ? String(t.project) : "",
    assigneeId: t.assignee?.id != null ? String(t.assignee.id) : "",
  });

  // Full (re)populate: when the modal opens, or opens on a different task.
  // Deliberately keyed on the task's id, not the object — a refreshed row
  // for the SAME task must not wipe what the user is typing (see below).
  useEffect(() => {
    if (!show) return;
    if (task) {
      const initial = formFromTask(task);
      setTitle(initial.title);
      setDescription(initial.description);
      setStatus(initial.status);
      setPriority(initial.priority);
      setDueDate(initial.dueDate);
      setProjectId(initial.projectId);
      setAssigneeId(initial.assigneeId);
      initialRef.current = initial;
    } else {
      setTitle(defaultTitle);
      setDescription(defaultDescription);
      setStatus(TASK_STATUS.TODO);
      setPriority(500);
      setDueDate("");
      setProjectId(defaultProjectId != null ? String(defaultProjectId) : "");
      setAssigneeId("");
      initialRef.current = null;
    }
    setFieldErrors({});
    setFormError(null);
    setConfirmDelete(false);
    setConflicts({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, taskId, defaultProjectId, defaultTitle, defaultDescription]);

  // Partial refresh: the same task came back changed (a claim or release
  // resolved, or a background refetch). Only status and assignee are the
  // server's to move under the user's feet — take those, and update the
  // baseline so they don't count as the user's edits. Title, description,
  // priority, due date, project stay as typed.
  const serverStatus = task?.status;
  const serverAssigneeId =
    task?.assignee?.id != null ? String(task.assignee.id) : "";
  useEffect(() => {
    if (!show || !task || !initialRef.current) return;
    const initial = initialRef.current;
    if (initial.status !== serverStatus) {
      setStatus(serverStatus || TASK_STATUS.TODO);
      initial.status = serverStatus || TASK_STATUS.TODO;
    }
    if (initial.assigneeId !== serverAssigneeId) {
      setAssigneeId(serverAssigneeId);
      initial.assigneeId = serverAssigneeId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, taskId, serverStatus, serverAssigneeId]);

  // Live fields (projects/task-live-refresh D8): the board keeps `task`
  // fresh while the modal is open. For the fields that are the USER's to
  // edit, a change from elsewhere is taken quietly when the field is
  // untouched here, and becomes a conflict when both sides changed it
  // differently — your typing is never overwritten behind your back.
  // (Status and assignee keep the rule above: the server's to move.)
  const serverForm = task ? formFromTask(task) : null;
  const serverKey = serverForm
    ? LIVE_FIELDS.map((f) => serverForm[f]).join("\u0000")
    : "";
  const fieldSetters = {
    title: setTitle,
    description: setDescription,
    priority: setPriority,
    dueDate: setDueDate,
    projectId: setProjectId,
  };
  const currentForm = { title, description, priority, dueDate, projectId };
  useEffect(() => {
    if (!show || !serverForm || !initialRef.current) return;
    const initial = initialRef.current;
    // Recomputed from the server's CURRENT value every time — never merged
    // with the last notice: a change that was reverted there must drop its
    // conflict, and Load theirs must load what the server holds now, not
    // what it held a minute ago (review round, PR #328).
    const found = {};
    for (const field of LIVE_FIELDS) {
      const theirs = serverForm[field];
      if (sameValue(field, theirs, initial[field])) continue; // unchanged there
      const mine = currentForm[field];
      if (sameValue(field, mine, initial[field])) {
        fieldSetters[field](theirs); // untouched here: take theirs
        initial[field] = theirs;
      } else if (sameValue(field, mine, theirs)) {
        initial[field] = theirs; // the same edit on both sides
      } else {
        found[field] = theirs;
      }
    }
    setConflicts(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, taskId, serverKey]);

  const conflictFields = Object.keys(conflicts);
  // Either choice closes the notice, and the buttons go with it: hand the
  // keyboard to the first field it was about, not the page body.
  const focusField = (field) =>
    window.requestAnimationFrame(() =>
      document.getElementById(FIELD_IDS[field])?.focus(),
    );
  const loadTheirs = () => {
    const initial = initialRef.current;
    for (const [field, value] of Object.entries(conflicts)) {
      fieldSetters[field](value);
      if (initial) initial[field] = value;
    }
    focusField(conflictFields[0]);
    setConflicts({});
  };
  const keepMine = () => {
    // their value becomes the baseline, so Save sends yours over it
    const initial = initialRef.current;
    for (const [field, value] of Object.entries(conflicts)) {
      if (initial) initial[field] = value;
    }
    focusField(conflictFields[0]);
    setConflicts({});
  };

  // What the live thread brought in: re-list files / drawing links when
  // SOMEONE ELSE changed them (your own changes already re-listed).
  const handleNewEvents = (events) => {
    const others = events.filter(
      (e) => e.actor?.id == null || e.actor.id !== me,
    );
    if (others.some((e) => e.verb === "attached" || e.verb === "detached")) {
      setFilesKey((n) => n + 1);
    }
    if (
      others.some(
        (e) => e.verb === "drawing_linked" || e.verb === "drawing_unlinked",
      )
    ) {
      setDrawingsKey((n) => n + 1);
    }
  };

  const buildPayload = () => {
    const current = {
      title: title.trim(),
      description: description.trim(),
      status,
      priority: Number(priority),
      due_date: dueDate ? new Date(dueDate).toISOString() : null,
      project: projectId ? Number(projectId) : null,
      assignee_id: assigneeId ? Number(assigneeId) : null,
    };
    const initial = initialRef.current;
    if (!initial) return current; // create: send everything
    const baseline = {
      title: initial.title.trim(),
      description: initial.description.trim(),
      status: initial.status,
      priority: Number(initial.priority),
      due_date: initial.dueDate
        ? new Date(initial.dueDate).toISOString()
        : null,
      project: initial.projectId ? Number(initial.projectId) : null,
      assignee_id: initial.assigneeId ? Number(initial.assigneeId) : null,
    };
    const changed = {};
    for (const key of Object.keys(current)) {
      if (current[key] !== baseline[key]) changed[key] = current[key];
    }
    return changed;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (uploadsBusy) return; // Enter in a field submits too
    const payload = buildPayload();
    if (isEdit && Object.keys(payload).length === 0) {
      onHide(); // nothing changed — no request
      return;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);

    try {
      await onSave(payload);
      onHide();
    } catch (err) {
      // Shared extractor splits DRF `{detail}`/`non_field_errors` (form-level)
      // from per-field errors (`{ title: [...] }`), which the fields render inline.
      const { formError: banner, fieldErrors: fields } = parseApiError(
        err,
        "Failed to save task",
      );
      setFieldErrors(fields);
      setFormError(banner);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    doDelete();
  };

  const doDelete = async () => {
    if (!task) return;
    setDeleting(true);
    try {
      await onDelete(task.id);
      onHide();
    } catch (err) {
      // Same extractor as save: a 403 from a shared task ("Only the task's
      // owner or the project's owner may delete it.") reads as itself, not
      // as an axios status line.
      setFormError(parseApiError(err, "Failed to delete task").formError);
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleClose = () => {
    if (!saving && !deleting && !claimBusy && !uploadsBusy) onHide();
  };

  // Who's who, for the buttons. `me` unknown → don't hide anything; the
  // API is the authority and its 403 reads as itself via parseApiError.
  const ownerId = task?.user?.id ?? null;
  const projectOwnerId =
    task?.project != null
      ? (projects.find((p) => p.id === task.project)?.user?.id ?? null)
      : null;
  const isOwner = me == null || me === ownerId || me === projectOwnerId;
  const claim = task?.claimed_by ?? null;
  const canRelease = me == null || me === claim?.id || isOwner;
  const closed = CLOSED.has(status);
  const meInDirectory = me != null && users.some((u) => u.id === me);
  // The current assignee may be missing from the directory (it lists
  // active accounts; the directory fetch may have failed). Keep them
  // selectable so the select never silently shows "Unassigned" while the
  // form still holds them.
  const assigneeNotListed =
    task?.assignee && !users.some((u) => u.id === task.assignee.id)
      ? task.assignee
      : null;

  const runClaimAction = async (action, fallback) => {
    if (!task || !action) return;
    setClaimBusy(true);
    setFormError(null);
    try {
      await action(task.id);
      // my own claim/release wrote events: show them now, not on the
      // next poll (the thread is no longer keyed on updated_at)
      setThreadBump((n) => n + 1);
    } catch (err) {
      setFormError(parseApiError(err, fallback).formError);
    } finally {
      setClaimBusy(false);
    }
  };

  const fieldError = (name) => {
    const err = fieldErrors[name];
    if (!err) return null;
    return [].concat(err).join(" ");
  };

  const busy = saving || deleting || claimBusy || uploadsBusy;
  // Archive is for finished tasks as SAVED (not the form's unsaved status),
  // and it closes the dialog, so unsaved edits hold it.
  const canArchive =
    isEdit &&
    Boolean(onArchive) &&
    CLOSED.has(task.status) &&
    !task.archived_at;
  const hasUnsavedEdits = isEdit && Object.keys(buildPayload()).length > 0;

  // One field-error line, wired to its input with aria-describedby.
  const errorLine = (name) =>
    fieldError(name) ? (
      <p id={`task-${name}-error`} className="mt-1 text-sm text-destructive">
        {fieldError(name)}
      </p>
    ) : null;

  return (
    // Static backdrop + scrollable body: clicking outside never
    // closes; Escape and the X do, unless a request is in flight. The form
    // is the flex column and the body region scrolls — the shape task #28
    // fixed for the original modal, kept here on purpose.
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        showCloseButton={!busy}
        className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => busy && event.preventDefault()}
        // Focus the title, not the first focusable (which can be a Release
        // link on the meta line); the opener gets focus back on close.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus();
        }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
          data-slot="task-form"
        >
          <DialogHeader className="border-b px-6 py-4 text-left">
            <DialogTitle className="font-display text-xl">
              {isEdit ? "Edit task" : "New task"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              {isEdit ? "Edit this task's details" : "Create a task"}
            </DialogDescription>
          </DialogHeader>
          <div
            data-slot="dialog-body"
            className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
          >
            {formError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            {conflictFields.length > 0 && (
              <Alert className="mb-4" role="alert">
                <AlertDescription className="flex flex-wrap items-center gap-2">
                  <span className="flex-1">
                    Someone else changed the{" "}
                    {conflictFields.map((f) => FIELD_LABELS[f]).join(", ")}{" "}
                    while you were editing. <strong>Load theirs</strong>{" "}
                    replaces your edit with theirs; <strong>Keep mine</strong>{" "}
                    keeps yours — Save then writes it over theirs.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={loadTheirs}
                  >
                    Load theirs
                  </Button>
                  <Button type="button" size="sm" onClick={keepMine}>
                    Keep mine
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {isEdit && task.archived_at && (
              <Alert className="mb-3 py-2">
                <AlertDescription className="flex flex-wrap items-center gap-3">
                  <span className="flex-1 text-sm">
                    Archived{" "}
                    <span title={task.archived_at}>
                      {relativeTime(task.archived_at)}
                    </span>{" "}
                    {task.archived_by ? (
                      <>
                        by{" "}
                        <UserAvatar
                          user={task.archived_by}
                          size="xs"
                          decorative
                          className="mr-1 align-text-bottom"
                        />
                        {displayName(task.archived_by)}
                      </>
                    ) : (
                      "automatically"
                    )}{" "}
                    — it&apos;s off the board.{" "}
                    {task.status === TASK_STATUS.CANCELED
                      ? "Canceled tasks don't show on the board — change its status above and save to bring it back."
                      : `Restore puts it back in ${statusLabel(task.status)} for another ${ARCHIVE_AFTER_DAYS} days.`}
                  </span>
                  {onUnarchive && task.status !== TASK_STATUS.CANCELED && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() =>
                        runClaimAction(
                          onUnarchive,
                          "Failed to restore the task",
                        )
                      }
                    >
                      Restore
                    </Button>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {isEdit && (
              <div
                className="mb-3 flex flex-wrap gap-3 text-sm text-muted-foreground"
                data-testid="task-meta"
              >
                {task.created_by && (
                  <span>
                    Created by{" "}
                    <UserAvatar
                      user={task.created_by}
                      size="xs"
                      decorative
                      className="mr-1 align-text-bottom"
                    />
                    <strong>{displayName(task.created_by)}</strong>
                    {isAgent(task.created_by) && (
                      <>
                        {" "}
                        <Badge variant="secondary" className="font-normal">
                          agent
                        </Badge>
                      </>
                    )}
                  </span>
                )}
                {task.user && task.user.id !== task.created_by?.id && (
                  <span>
                    Owned by{" "}
                    <UserAvatar
                      user={task.user}
                      size="xs"
                      decorative
                      className="mr-1 align-text-bottom"
                    />
                    <strong>{displayName(task.user)}</strong>
                  </span>
                )}
                {claim ? (
                  <span>
                    Claimed by{" "}
                    <UserAvatar
                      user={claim}
                      size="xs"
                      decorative
                      className="mr-1 align-text-bottom"
                    />
                    <strong>{displayName(claim)}</strong>
                    {isAgent(claim) && (
                      <>
                        {" "}
                        <Badge variant="secondary" className="font-normal">
                          agent
                        </Badge>
                      </>
                    )}{" "}
                    <span title={task.claimed_at || ""}>
                      {relativeTime(task.claimed_at)}
                    </span>
                    {onRelease && canRelease && (
                      <Button
                        type="button"
                        size="sm"
                        variant="link"
                        className="ml-2 h-auto p-0 align-baseline"
                        disabled={busy}
                        onClick={() =>
                          runClaimAction(
                            onRelease,
                            "Failed to release the task",
                          )
                        }
                      >
                        Release
                      </Button>
                    )}
                  </span>
                ) : (
                  onClaim &&
                  !closed && (
                    <span>
                      Nobody is on this.{" "}
                      <Button
                        type="button"
                        size="sm"
                        variant="link"
                        className="h-auto p-0 align-baseline"
                        disabled={busy}
                        onClick={() =>
                          runClaimAction(onClaim, "Failed to claim the task")
                        }
                      >
                        Claim it
                      </Button>{" "}
                      <span>(marks it In Progress and assigns it to you)</span>
                    </span>
                  )
                )}
              </div>
            )}

            <div className="mb-4 grid gap-1.5">
              <Label htmlFor="task-title">Title</Label>
              <Input
                id="task-title"
                ref={titleRef}
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                aria-invalid={Boolean(fieldError("title")) || undefined}
                aria-describedby={
                  fieldError("title") ? "task-title-error" : undefined
                }
                required
              />
              {errorLine("title")}
            </div>

            <div className="mb-4 grid gap-1.5">
              <Label htmlFor="task-description">Description</Label>
              <Textarea
                id="task-description"
                className="max-h-48"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                aria-invalid={Boolean(fieldError("description")) || undefined}
                aria-describedby={
                  fieldError("description")
                    ? "task-description-error"
                    : undefined
                }
              />
              {errorLine("description")}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="task-status">Status</Label>
                <NativeSelect
                  id="task-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </NativeSelect>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="task-priority">Priority</Label>
                <Input
                  id="task-priority"
                  type="number"
                  min={0}
                  max={1000}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  aria-invalid={Boolean(fieldError("priority")) || undefined}
                  aria-describedby={
                    fieldError("priority") ? "task-priority-error" : undefined
                  }
                />
                {errorLine("priority")}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="task-due-date">Due date</Label>
                <Input
                  id="task-due-date"
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="task-project">Project</Label>
                <NativeSelect
                  id="task-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">No project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={String(project.id)}>
                      {project.name}
                      {project.visibility === "household"
                        ? " (household)"
                        : project.visibility === "people"
                          ? " (shared)"
                          : ""}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            </div>

            <div className="mt-4 grid gap-1.5">
              <div className="flex items-end justify-between">
                <Label htmlFor="task-assignee">Assigned to</Label>
                {meInDirectory && String(me) !== assigneeId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="h-auto p-0"
                    onClick={() => setAssigneeId(String(me))}
                  >
                    Assign to me
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* The chosen person's face beside the picker — native
                  options can't hold images. */}
                <UserAvatar
                  user={
                    users.find((u) => String(u.id) === assigneeId) ||
                    (assigneeNotListed &&
                    String(assigneeNotListed.id) === assigneeId
                      ? assigneeNotListed
                      : null)
                  }
                  size="sm"
                  decorative
                  className={assigneeId ? "" : "invisible"}
                />
                <NativeSelect
                  id="task-assignee"
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  aria-invalid={Boolean(fieldError("assignee_id")) || undefined}
                  aria-describedby={
                    fieldError("assignee_id")
                      ? "task-assignee_id-error"
                      : undefined
                  }
                >
                  <option value="">Unassigned</option>
                  {assigneeNotListed && (
                    <option value={String(assigneeNotListed.id)}>
                      {userLabel(assigneeNotListed)} — not in the directory
                    </option>
                  )}
                  {users.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {userLabel(u)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              {fieldError("assignee_id") ? (
                errorLine("assignee_id")
              ) : usersError ? (
                <p className="text-sm text-destructive">
                  Couldn&apos;t load the household directory.{" "}
                  {onRetryUsers && (
                    <Button
                      type="button"
                      size="sm"
                      variant="link"
                      className="h-auto p-0 align-baseline"
                      onClick={onRetryUsers}
                    >
                      Retry
                    </Button>
                  )}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Anyone who can see this task — people and agents. Outside a
                  household project, only the task&apos;s owner or the
                  project&apos;s owner.
                </p>
              )}
            </div>

            {isEdit && (
              <TaskAttachments
                key={task.id}
                taskId={task.id}
                me={me}
                canManage={isOwner}
                onChange={() => {
                  setThreadBump((n) => n + 1);
                  onAttachmentsChange?.();
                }}
                onBusyChange={setUploadsBusy}
                refreshKey={filesKey}
              />
            )}

            {isEdit && (
              <TaskDrawings
                key={`drawings-${task.id}`}
                taskId={task.id}
                onChange={() => setThreadBump((n) => n + 1)}
                refreshKey={drawingsKey}
              />
            )}

            {isEdit && (
              <TaskActivity
                taskId={task.id}
                refreshKey={String(threadBump)}
                onNewEvents={handleNewEvents}
              />
            )}
          </div>
          <DialogFooter className="border-t px-6 py-4 sm:justify-between">
            <div className="flex flex-wrap gap-2">
              {canArchive && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || hasUnsavedEdits}
                  title={
                    hasUnsavedEdits
                      ? "Save or discard your changes first — archiving closes this dialog"
                      : `Take it off the board now (it would go by itself ${ARCHIVE_AFTER_DAYS} days after finishing)`
                  }
                  onClick={() =>
                    runClaimAction(onArchive, "Failed to archive the task")
                  }
                >
                  Archive
                </Button>
              )}
              {isEdit && isOwner && !confirmDelete && (
                <Button
                  type="button"
                  variant="outline"
                  className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={handleDeleteClick}
                  disabled={saving || deleting}
                >
                  Delete
                </Button>
              )}
              {isEdit && isOwner && confirmDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDeleteClick}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Confirm delete"
                  )}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || deleting || uploadsBusy || !title.trim()}
              >
                {uploadsBusy ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Waiting for uploads…
                  </>
                ) : saving ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Saving...
                  </>
                ) : isEdit ? (
                  "Save"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

TaskModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  projects: PropTypes.array.isRequired,
  task: PropTypes.object,
  defaultProjectId: PropTypes.number,
  // Create mode only: what the form starts with ("Make this a task" from a
  // discussion post).
  defaultTitle: PropTypes.string,
  defaultDescription: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  users: PropTypes.array,
  usersError: PropTypes.object,
  onRetryUsers: PropTypes.func,
  me: PropTypes.number,
  onClaim: PropTypes.func,
  onRelease: PropTypes.func,
  onAttachmentsChange: PropTypes.func,
  onArchive: PropTypes.func,
  onUnarchive: PropTypes.func,
  onCloseAutoFocus: PropTypes.func,
};

export default TaskModal;
